"use client";

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";

import { EngageChatInterface } from "./EngageChatInterface";
import { recordWidgetManualPanelClose } from "./auto-launch-storage";
import type { WidgetConfig, WidgetPosition } from "./widget-config";
import { useResponsiveWidgetPosition } from "./use-responsive-widget-position";
import { useWidgetMobileLayout } from "./use-widget-mobile-layout";
import { WIDGET_SHADOW_CSS } from "widget-shadow-css";
import {
  resolveLauncherButtonClassName,
  resolveLauncherButtonStyle,
  resolvePanelBorderRadius,
} from "@/lib/engage/widget-appearance";

export type { WidgetConfig } from "./widget-config";

export interface SkinnyWidgetAppProps {
  readonly config: WidgetConfig;
}

type BodyPanelHostMode = "anchored-button-panel" | "floating-mobile";

function bodyPanelHostAttr(mode: BodyPanelHostMode): string {
  return mode === "floating-mobile" ? "widget-floating-mobile-host" : "widget-anchored-panel-host";
}

function attachBodyPanelHost(mode: BodyPanelHostMode): { host: HTMLElement; mount: HTMLElement } {
  const host = document.createElement("div");
  host.setAttribute("data-s2r-component", bodyPanelHostAttr(mode));
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: "open" });
  const styleEl = document.createElement("style");
  styleEl.setAttribute("data-s2r-component", "widget-shadow-tailwind");
  styleEl.textContent = WIDGET_SHADOW_CSS;
  shadow.appendChild(styleEl);

  const mount = document.createElement("div");
  mount.setAttribute("data-s2r-component", "public-widget-panel-root");
  shadow.appendChild(mount);
  return { host, mount };
}

/**
 * Anchored-button mode (#Advanced / TSAI parity) renders the launcher button
 * in normal flow inside the customer's target element, but the chat panel must
 * slide over from a body-level overlay so it is never clipped or trapped by
 * the anchor element's containing block (TSAI's recommended container snippet
 * uses `transform`, which would otherwise capture a `position: fixed` panel).
 *
 * Floating mode on mobile also portals to `document.body` — iOS Safari does not
 * reliably paint `position: fixed` launchers inside the widget shadow root (#1154).
 *
 * Host attach is layout-synchronous (not a queued setState). A microtask lost
 * the portal target when dashboard teardown swept body hosts after the FAB
 * mounted — Ask Engage expanded with no chat.
 */
function useBodyPanelHost(
  active: boolean,
  mode: BodyPanelHostMode,
  panelOpen: boolean,
): HTMLElement | null {
  const hostRef = useRef<HTMLElement | null>(null);
  const mountRef = useRef<HTMLElement | null>(null);
  const listenersRef = useRef(new Set<() => void>());

  const notify = useCallback(() => {
    listenersRef.current.forEach((listener) => {
      listener();
    });
  }, []);

  const subscribe = useCallback((onStoreChange: () => void) => {
    listenersRef.current.add(onStoreChange);
    return () => {
      listenersRef.current.delete(onStoreChange);
    };
  }, []);

  const getSnapshot = useCallback(() => {
    const el = mountRef.current;
    return el?.isConnected ? el : null;
  }, []);

  useLayoutEffect(() => {
    if (!active || typeof document === "undefined") {
      if (hostRef.current) {
        hostRef.current.remove();
        hostRef.current = null;
        mountRef.current = null;
        notify();
      }
      return undefined;
    }

    const { host, mount } = attachBodyPanelHost(mode);
    hostRef.current = host;
    mountRef.current = mount;
    notify();

    return () => {
      const current = hostRef.current;
      current?.remove();
      if (hostRef.current === current) {
        hostRef.current = null;
        mountRef.current = null;
      }
      notify();
    };
  }, [active, mode, notify]);

  useLayoutEffect(() => {
    if (!active || !panelOpen || typeof document === "undefined") {
      return undefined;
    }
    if (mountRef.current?.isConnected) {
      return undefined;
    }
    const { host, mount } = attachBodyPanelHost(mode);
    hostRef.current = host;
    mountRef.current = mount;
    notify();
    return undefined;
  }, [active, mode, panelOpen, notify]);

  return useSyncExternalStore(subscribe, getSnapshot, () => null);
}

const POSITION_CLASSES: Record<WidgetPosition, string> = {
  "bottom-right": "bottom-[max(1.25rem,env(safe-area-inset-bottom))] right-[max(1.25rem,env(safe-area-inset-right))]",
  "bottom-left": "bottom-[max(1.25rem,env(safe-area-inset-bottom))] left-[max(1.25rem,env(safe-area-inset-left))]",
  "top-right": "top-[max(1.25rem,env(safe-area-inset-top))] right-[max(1.25rem,env(safe-area-inset-right))]",
  "top-left": "top-[max(1.25rem,env(safe-area-inset-top))] left-[max(1.25rem,env(safe-area-inset-left))]",
};

/** FAB-only hit target — shell stays `pointer-events-none` so closed panel never shields the page (TSAI parity). */
const WIDGET_LAUNCHER_HIT_CLASS = "pointer-events-auto";

const WIDGET_PANEL_OPEN_CLASS =
  "pointer-events-auto flex max-h-[min(560px,calc(100dvh-6rem))] w-[min(100vw-1.5rem,380px)] flex-col overflow-hidden border border-zinc-200 shadow-2xl dark:border-zinc-800";

const WIDGET_PANEL_MOBILE_SHEET_CLASS =
  "pointer-events-auto flex h-full w-full flex-col overflow-hidden border-0 shadow-2xl";

function widgetMobileShellStyle(
  viewport: NonNullable<ReturnType<typeof useWidgetMobileLayout>["viewport"]>,
): CSSProperties {
  return {
    top: viewport.top,
    left: viewport.left,
    width: viewport.width,
    height: viewport.height,
  };
}

function useSystemDarkPreference(): boolean {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      setIsDark(mq.matches);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  return isDark;
}

export function SkinnyWidgetApp({ config }: SkinnyWidgetAppProps) {
  // The loader leaves `config.targetId` set only when a mount target resolved
  // to a real element (otherwise it strips it for the floating fallback).
  // `targetMode` then selects the layout:
  //  - panel (#117): inline chat panel inside the target element, no FAB.
  //  - button (Advanced / TSAI parity): launcher button in-flow inside the
  //    target element + body-level slide-over panel.
  const hasTarget = typeof config.targetId === "string" && config.targetId.length > 0;
  const inlineMode = hasTarget && config.targetMode === "panel";
  const anchoredButtonMode = hasTarget && config.targetMode === "button";
  const floatingMode = !inlineMode && !anchoredButtonMode;
  const [panelOpen, setPanelOpen] = useState<boolean>(inlineMode);
  const systemDark = useSystemDarkPreference();
  const { isMobile, viewport: mobileViewport } = useWidgetMobileLayout(panelOpen);
  const anchoredBodyPanelMount = useBodyPanelHost(
    anchoredButtonMode,
    "anchored-button-panel",
    panelOpen,
  );
  const floatingMobileBodyMount = useBodyPanelHost(
    floatingMode && isMobile,
    "floating-mobile",
    panelOpen,
  );

  const effectiveDark = useMemo(() => {
    if (config.theme === "dark") {
      return true;
    }
    if (config.theme === "light") {
      return false;
    }
    return systemDark;
  }, [config.theme, systemDark]);

  const effectivePosition = useResponsiveWidgetPosition(config.position, {
    positionDesktop: config.positionDesktop,
    positionMobile: config.positionMobile,
    positionTablet: config.positionTablet,
  });

  const positionClass = POSITION_CLASSES[effectivePosition];
  const mobileSheetOpen = isMobile && panelOpen && mobileViewport !== null;

  const panelShellStyle: CSSProperties = useMemo(() => {
    return {
      backgroundColor: config.surfaceColor,
      borderRadius: resolvePanelBorderRadius(config.cornerRadius),
      "--s2r-widget-user-bubble": config.userBubbleColor,
      "--s2r-widget-reply-bubble": config.replyBubbleColor,
    };
  }, [
    config.cornerRadius,
    config.replyBubbleColor,
    config.surfaceColor,
    config.userBubbleColor,
  ]);

  const fabClassName = useMemo(
    () =>
      resolveLauncherButtonClassName({
        launcherRadius: config.launcherRadius,
        launcherTextColor: config.launcherTextColor,
      }),
    [config.launcherRadius, config.launcherTextColor],
  );

  const fabStyle = useMemo(
    () =>
      resolveLauncherButtonStyle({
        launcherFillColor: config.launcherFillColor,
        launcherTextColor: config.launcherTextColor,
        launcherRadius: config.launcherRadius,
      }),
    [config.launcherFillColor, config.launcherRadius, config.launcherTextColor],
  );

  const togglePanel = useCallback(() => {
    setPanelOpen((wasOpen) => {
      if (wasOpen) {
        recordWidgetManualPanelClose(config.tenantId, config.siteId);
      }
      return !wasOpen;
    });
  }, [config.siteId, config.tenantId]);

  const renderPanel = (options: {
    readonly panelClassName: string;
    readonly onClosePanel: () => void;
  }) => (
    <div
      aria-hidden={!panelOpen}
      className={options.panelClassName}
      data-s2r-component="widget-panel"
      data-s2r-mobile-sheet={mobileSheetOpen ? "true" : undefined}
      style={panelShellStyle}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <EngageChatInterface
          config={config}
          panelOpen={panelOpen}
          mobileSheetOpen={mobileSheetOpen}
          onRequestOpenPanel={() => setPanelOpen(true)}
          onClosePanel={options.onClosePanel}
        />
      </div>
    </div>
  );

  if (inlineMode) {
    // In-place layout (#117): no fixed positioning, no FAB. The widget
    // host page owns the box; the panel fills the container width and
    // grows to a reasonable max height while keeping the chat scrollable.
    // Visitors never close this surface — `onClosePanel` is a no-op so
    // the EngageChatInterface header close affordance is suppressed.
    return (
      <div
        className={`pointer-events-auto flex w-full flex-col ${effectiveDark ? "dark" : ""}`}
        data-s2r-component="skinny-widget-app"
        data-s2r-mount-mode="inline"
      >
        <div
          className="flex h-[min(640px,80vh)] w-full flex-col overflow-hidden border border-zinc-200 shadow-sm dark:border-zinc-800"
          data-s2r-component="widget-panel"
          style={panelShellStyle}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <EngageChatInterface
              config={config}
              panelOpen
              onRequestOpenPanel={() => setPanelOpen(true)}
              onClosePanel={() => {
                /* inline mount: panel is always open; close is a no-op */
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (anchoredButtonMode) {
    // Advanced placement (TSAI parity): the launcher button renders in normal
    // flow inside the customer's target element; the panel slides over from a
    // body-level overlay (portaled into `bodyPanelMount`) so it is never
    // clipped by the anchor element's containing block.
    return (
      <div
        className={`pointer-events-auto inline-flex ${effectiveDark ? "dark" : ""}`}
        data-s2r-component="skinny-widget-app"
        data-s2r-mount-mode="anchored-button"
      >
        <button
          aria-expanded={panelOpen}
          aria-haspopup="dialog"
          className={`${fabClassName} ${WIDGET_LAUNCHER_HIT_CLASS}`}
          data-s2r-component="widget-fab"
          style={fabStyle}
          type="button"
          onClick={togglePanel}
        >
          {config.launcherButtonText}
        </button>

        {anchoredBodyPanelMount
          ? createPortal(
              <div
                className={`pointer-events-none fixed z-[2147483646] flex flex-col ${
                  mobileSheetOpen
                    ? ""
                    : "bottom-5 right-5 items-end"
                } ${effectiveDark ? "dark" : ""}`}
                data-s2r-component="skinny-widget-app"
                data-s2r-mount-mode="anchored-button-panel"
                style={mobileSheetOpen ? widgetMobileShellStyle(mobileViewport) : undefined}
              >
                {renderPanel({
                  panelClassName: `${mobileSheetOpen ? WIDGET_PANEL_MOBILE_SHEET_CLASS : WIDGET_PANEL_OPEN_CLASS} transition-all duration-300 ease-out ${effectiveDark ? "dark" : ""} ${
                    panelOpen
                      ? mobileSheetOpen
                        ? "opacity-100"
                        : "translate-x-0 opacity-100"
                      : "hidden"
                  }`,
                  onClosePanel: () => setPanelOpen(false),
                })}
              </div>,
              anchoredBodyPanelMount,
            )
          : null}
      </div>
    );
  }

  const floatingWidget = (
    <div
      className={`pointer-events-none fixed z-[2147483646] ${
        mobileSheetOpen ? "" : `${positionClass} items-end gap-3`
      } flex flex-col`}
      data-s2r-component="skinny-widget-app"
      data-s2r-mount-mode="floating"
      style={mobileSheetOpen ? widgetMobileShellStyle(mobileViewport) : undefined}
    >
      {renderPanel({
        panelClassName: `${mobileSheetOpen ? WIDGET_PANEL_MOBILE_SHEET_CLASS : WIDGET_PANEL_OPEN_CLASS} ${effectiveDark ? "dark" : ""} ${
          panelOpen ? "opacity-100" : "hidden"
        }`,
        onClosePanel: () => setPanelOpen(false),
      })}

      {mobileSheetOpen ? null : (
        <button
          aria-expanded={panelOpen}
          aria-haspopup="dialog"
          className={`${fabClassName} ${WIDGET_LAUNCHER_HIT_CLASS}`}
          data-s2r-component="widget-fab"
          style={fabStyle}
          type="button"
          onClick={togglePanel}
        >
          {config.launcherButtonText}
        </button>
      )}
    </div>
  );

  if (floatingMode && isMobile && floatingMobileBodyMount) {
    return createPortal(floatingWidget, floatingMobileBodyMount);
  }

  return floatingWidget;
}
